import { Injectable, OnDestroy, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject, timer } from 'rxjs';
import { map, shareReplay, skipWhile, switchMap, takeUntil, tap } from 'rxjs/operators';
import { find } from 'lodash';

import { OrderStoreSelector } from '@common/selectors/order.selector';
import { DateUtils } from '@shared/utils/date/date.utils';
import { DoodOrderModel, DoodOrderStatus } from '@store/order/order.model';
import { OrdersService } from '@core/services/orders/orders.service';

import {
  KIOSK_ORDER_STATUS_BLOCK_LOADING_VM,
  KioskOrderStatusBlockDataSource,
  KioskOrderStatusBlockViewModel,
} from './kiosk-order-status-block-view-model';

function toViewModel(order?: DoodOrderModel): KioskOrderStatusBlockViewModel {
  if (!order) {
    return KIOSK_ORDER_STATUS_BLOCK_LOADING_VM;
  }

  return {
    order,
    orderReadyAt: order.ready_at
      ? DateUtils.dayjsInMarketplaceTimezone(order.ready_at).format('HH[h]mm')
      : '',
    receiptPrinted: !!find(order.metafields, {
      handle: 'kiosk_receipt_printed_at',
    }),
  };
}

@Injectable()
export class KioskOrderStatusBlockStoreDataSource
  implements KioskOrderStatusBlockDataSource, OnDestroy
{
  private readonly orderSelector = inject(OrderStoreSelector);
  private readonly ordersService = inject(OrdersService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly destroy$ = new Subject<void>();
  private readonly orderId = this.activatedRoute.snapshot.params.orderId;
  private currentOrder?: DoodOrderModel;

  private readonly order$ = this.orderSelector.selectOrder(this.orderId).pipe(
    tap(order => {
      this.currentOrder = order;
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly vm$ = this.order$.pipe(map(order => toViewModel(order)));

  constructor() {
    this.order$.pipe(takeUntil(this.destroy$)).subscribe();

    timer(1000, 5000)
      .pipe(
        takeUntil(this.destroy$),
        skipWhile(() => !this.currentOrder || this.currentOrder.status !== DoodOrderStatus.payment),
        switchMap(() => this.ordersService.loadOrderById$(this.orderId)),
        tap(order => this.ordersService.setActiveOrderToStore(order)),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
